import {
  ChangeEvent,
  useContext, useEffect, useMemo, useState,
} from 'react';
import Link from 'next/link';
import {
  Grid, Card, Typography, CardContent, Box, Pagination,
  Button,
} from '@mui/material';
import { sendGTMEvent } from '@next/third-parties/google';
import BusinessIcon from '@mui/icons-material/Business';

import { colorVariables, pageLimit } from '@/constants';
import { SearchContext } from '@/page-components/Search';
import { Vendor } from '@/lib/types/interfaces/Vendor';
import SearchLoaderSkeleton from '@/components/SearchLoaderSkeleton';
import usePagination from '@/hooks/usePagination';
import { useQuery } from '@/hooks/useQuery';
import { SearchLayoutContext } from '../SearchLayout/SearchLayout';
import theme from '@/theme';

const SearchResults = () => {
  const { searchData, searchTotal } = useContext(SearchContext);
  const [search, setSearch] = useState<Vendor[] | null>(searchData || null);
  const { queryParams } = useQuery();
  const {
    currentPage, jump, maxPage, setCurrentPage,
  } = usePagination(pageLimit, searchTotal);
  const handleChangePage = (_: ChangeEvent<unknown>, page: number) => {
    jump(page);
  };
  const {
    term,
    location,
  } = useContext(SearchLayoutContext);

  useEffect(() => {
    setSearch(searchData || null);
    queryParams.has('page') ? setCurrentPage(Number(queryParams.get('page')?.toString())) : setCurrentPage(1);
  }, [searchData]);

  const handleOnServiceClick = () => {
    sendGTMEvent({
      event: 'click_on_service_provider_card_from_a_search',
      search_term: term || '',
      search_location: location ? `${location?.name}, ${location?.state}` : '',
      category: '',
      radius: queryParams.get('radius') || '',
    });
  };

  const renderItems = useMemo(() => (search?.map((item: Vendor) => (
    <Grid
      item
      lg={6}
      md={6}
      xs={12}
      key={`search-${item.id}`}
      width={{ xs: '100%' }}
    >
      <Link
        href={`/service/${item.slug}`}
        aria-label='Service detail'
        passHref
        legacyBehavior
        prefetch={false}
      >
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,
        jsx-a11y/click-events-have-key-events,
        jsx-a11y/no-static-element-interactions */}
        <a
          target='_blank'
          rel='noopener'
          onClick={handleOnServiceClick}
        >
          <Card sx={{
            boxShadow: 'unset', border: `1px solid ${colorVariables.separatorColor}`, borderRadius: '16px', height: '100%',
          }}
          >
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '100%',
              '&:last-child': {
                paddingBottom: 2,
              },
            }}
            >
              <Box display='flex'>
                <Box
                  display='flex'
                  width={64}
                  height={64}
                  marginRight={2}
                  borderRadius='50%'
                >
                  {!item.photo ? (
                    <BusinessIcon
                      sx={{
                        width: 'inherit',
                        height: 'inherit',
                        fill: '#E6E7E6',
                      }}
                    />
                  ) : (
                    <img
                      src={item.photo}
                      alt={item.business_name}
                      width={64}
                      height={64}
                      style={{ borderRadius: 'inherit' }}
                    />
                  )}
                </Box>
                <Box>
                  <Typography
                    gutterBottom
                    variant='h5'
                    component='h3'
                    fontWeight={600}
                  >
                    {item.business_name}
                  </Typography>
                  <Typography
                    variant='body1'
                    color={colorVariables.textDescription}
                  >
                    {item.city}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant='body1'
                fontWeight={400}
                marginTop={1}
                color={colorVariables.textDescription}
              >
                {item.description_short}
              </Typography>
              <Box marginTop={2}>
                <Button
                  variant='outlined'
                  sx={{
                    minHeight: 40,
                    width: 144,
                    fontSize: '15px',
                    lineHeight: '26px',
                    letterSpacing: '0.46px',
                    fontWeight: 500,
                  }}
                >
                  View Profile
                </Button>
              </Box>
            </CardContent>

          </Card>
        </a>
      </Link>
    </Grid>
  ))), [search, term, location]);

  return (
    <Box
      padding={2}
      display='flex'
      flex={1}
      flexDirection='column'
      sx={{
        paddingX: 3,
        [theme.breakpoints.up('lg')]: {
          paddingX: 18,
        },
      }}
    >
      {Object.is(search, null) ? <SearchLoaderSkeleton /> : (
        searchData?.length ? (
          <Grid
            container
            spacing={3}
          >
            {renderItems}
          </Grid>
        ) : (
          null
        )
      )}
      {searchTotal && searchTotal > pageLimit ? (
        <Box
          display='flex'
          justifyContent='center'
          marginTop={4}
          marginBottom={4}
        >
          <Pagination
            count={maxPage}
            page={currentPage}
            onChange={handleChangePage}
            sx={{
              ul: {
                '& .MuiPaginationItem-root': {
                  '&.Mui-selected': {
                    color: 'white',
                  },
                },
              },
            }}
            color='primary'
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default SearchResults;
